import { useQuery } from "@tanstack/react-query";

import { getAccessTokenCookie } from "../../utils/cookies";
import { getEnv } from "../../utils/env";
import type { ReportFinalisationState } from "../mutations/use-report-finalisation";

/**
 * Finalisation / Vinci-upload state for a report response (MDRS-271).
 *
 * Drives the doctor's "Report Finalised" pill (on reload) and the admin
 * finalisation controls (download / mark-uploaded / unlock). Readable by the
 * owner or an admin; hand-rolled `fetch` to match the finalisation mutations.
 */
export const FINALISATION_STATE_KEY = "finalisation-state";

export const useFinalisationState = (
  responseId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: [FINALISATION_STATE_KEY, responseId],
    enabled: enabled && !!responseId,
    queryFn: async (): Promise<ReportFinalisationState> => {
      const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
      const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/responses/${encodeURIComponent(
        responseId as string
      )}/finalisation-state`;
      const token = getAccessTokenCookie();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        throw new Error(
          `Failed to load finalisation state (${resp.status}): ${detail || resp.statusText}`
        );
      }
      return resp.json();
    },
  });
